// src/pages/UseCaseResults.jsx - PRODUCTION VERSION WITH TRIAGE SCORES
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '@/config/appConfig';
import UseCasePreview from '../components/UseCasePreview';
import FiltersToolbar from '../components/FiltersToolbar';

export default function UseCaseResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Core state
  const [allUseCases, setAllUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [hoveredUseCase, setHoveredUseCase] = useState(null);
  
  // Filter state
  const [categoryMode, setCategoryMode] = useState(searchParams.get('mode') || 'core');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'all');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'total');
  
  // Combined filters state for toolbar
  const [filters, setFilters] = useState({
    type: selectedType,
    category: selectedCategory,
    categoryMode: categoryMode
  });

  // Top performers from triage scoring
  const TOP_10_IDS = [37, 22, 31, 30, 32, 47, 34, 40, 411, 55];

  // Fetch all use cases with client category mappings once on mount
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const clientId = localStorage.getItem('dva_current_client') || '1';
        const response = await fetch(buildApiUrl(`/api/v2/useCases/list?clientId=${clientId}`));
        
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const data = await response.json();
        const cases = data.data || data.useCases || data.use_cases || data || [];
        
        // Process use cases to add metadata
        const processedCases = cases.map(uc => {
          // Check if this is a top performer
          const isTopPerformer = TOP_10_IDS.includes(uc.use_case_id);
          
          // Check if financial gate was applied (score exactly 40 without ROI model)
          const hasFinancialGate = uc.total_score === 40 && !uc.has_roi_model;
          
          return {
            ...uc,
            is_top_performer: isTopPerformer,
            has_financial_gate: hasFinancialGate,
            confidence_level: uc.confidence_level || 'medium' // Default to medium if not provided
          };
        });
        
        setAllUseCases(Array.isArray(processedCases) ? processedCases : []);
        
      } catch (err) {
        console.error('Error fetching use cases:', err);
        setError(err.message);
        setAllUseCases([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Create a stable fetchDetails function
  const fetchDetails = useCallback(async (id) => {
    if (!id) return null;

    try {
      const response = await fetch(buildApiUrl(`/api/v2/useCases/${id}`));
      if (!response.ok) throw new Error('Failed to fetch details');
      return await response.json();
    } catch (err) {
      console.error('Error fetching use case details:', err);
      return null;
    }
  }, []);

  // Extract CORE categories
  const uniqueCoreCategories = useMemo(() => {
    const cats = new Set();
    allUseCases.forEach(uc => {
      const category = uc.category_name || uc.category;
      if (category && typeof category === 'string') {
        cats.add(category.trim());
      }
    });
    return ['all', ...Array.from(cats).sort()];
  }, [allUseCases]);

  // Extract CLIENT categories
  const uniqueClientCategories = useMemo(() => {
    const clientCats = new Set();
    
    allUseCases.forEach(uc => {
      if (uc.client_category_names && Array.isArray(uc.client_category_names)) {
        uc.client_category_names.forEach(cat => {
          if (cat && typeof cat === 'string') {
            clientCats.add(cat.trim());
          }
        });
      }
    });
    
    return ['all', ...Array.from(clientCats).sort()];
  }, [allUseCases]);

  // Get unique types for filter
  const uniqueTypes = useMemo(() => {
    const types = new Set();
    allUseCases.forEach(uc => {
      const type = uc.type || uc.priority_label;
      if (type) types.add(type);
    });
    return ['all', ...Array.from(types).sort()];
  }, [allUseCases]);

  // When category mode changes, reset selected category if it's not in the new list
  useEffect(() => {
    const currentList = categoryMode === 'client' ? uniqueClientCategories : uniqueCoreCategories;
    if (!currentList.includes(selectedCategory)) {
      setSelectedCategory('all');
      setFilters(prev => ({ ...prev, category: 'all' }));
    }
  }, [categoryMode, uniqueClientCategories, uniqueCoreCategories, selectedCategory]);

  // Filter and sort use cases
  const processedUseCases = useMemo(() => {
    let filtered = allUseCases.filter(uc => {
      // Type filter
      const typeMatch = selectedType === 'all' || 
        uc.type === selectedType || 
        uc.priority_label === selectedType;
      
      // Category filter - different logic for core vs client
      let categoryMatch = true;
      if (categoryMode === 'client') {
        // Client category filtering
        if (selectedCategory !== 'all') {
          categoryMatch = uc.client_category_names && 
            Array.isArray(uc.client_category_names) &&
            uc.client_category_names.includes(selectedCategory);
        }
      } else {
        // Core category filtering
        categoryMatch = selectedCategory === 'all' || 
          uc.category === selectedCategory || 
          uc.category_name === selectedCategory;
      }
      
      // Search filter
      const searchMatch = !searchQuery || 
        (uc.title || uc.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (uc.category || uc.category_name || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      return typeMatch && categoryMatch && searchMatch;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'value':
          return (b.value_score || 0) - (a.value_score || 0);
        case 'ease':
          return (b.implementation_score || 0) - (a.implementation_score || 0);
        case 'category':
          return (a.category || a.category_name || '').localeCompare(b.category || b.category_name || '');
        case 'type':
          return (a.type || a.priority_label || '').localeCompare(b.type || b.priority_label || '');
        case 'total':
        default:
          return (b.total_score || 0) - (a.total_score || 0);
      }
    });

    return filtered;
  }, [allUseCases, selectedType, selectedCategory, searchQuery, sortBy, categoryMode]);

  // Update URL
  const updateURL = (key, value) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value && value !== 'all' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
      return newParams;
    });
  };

  // Handlers
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setSelectedType(newFilters.type);
    setSelectedCategory(newFilters.category);
    setCategoryMode(newFilters.categoryMode || 'core');
    
    // Update URL parameters
    updateURL('type', newFilters.type);
    updateURL('category', newFilters.category);
    updateURL('mode', newFilters.categoryMode);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    updateURL('q', value);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    updateURL('sort', value);
  };

  const handleSelectUseCase = (useCase) => {
    setSelectedUseCase(useCase);
    if (useCase) {
      updateURL('uc', useCase.use_case_id);
    } else {
      updateURL('uc', null);
    }
  };

  const handleClear = useCallback(() => {
    setSelectedUseCase(null);
    updateURL('uc', null);
  }, []);

  const handleStep = useCallback((direction) => {
    if (!selectedUseCase) return;
    
    const currentIdx = processedUseCases.findIndex(uc => uc.use_case_id === selectedUseCase.use_case_id);
    const nextIdx = currentIdx + direction;
    
    if (nextIdx >= 0 && nextIdx < processedUseCases.length) {
      const nextUseCase = processedUseCases[nextIdx];
      setSelectedUseCase(nextUseCase);
      updateURL('uc', nextUseCase.use_case_id);
    }
  }, [selectedUseCase, processedUseCases]);

  const handleViewProfile = useCallback((useCase) => {
    if (useCase && useCase.use_case_id) {
      navigate(`/usecase/${useCase.use_case_id}`);
    }
  }, [navigate]);

  // Get confidence badge color
  const getConfidenceColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'high': return { bg: '#dcfce7', border: '#86efac', text: '#166534' };
      case 'medium': return { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' };
      case 'low': return { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' };
      default: return { bg: '#f3f4f6', border: '#e5e7eb', text: '#6b7280' };
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#6b7280', marginBottom: 8 }}>Loading triage scores...</div>
          <div style={{ fontSize: 14, color: '#9ca3af' }}>Processing {allUseCases.length || 76} use cases</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h1 style={{ 
            fontSize: 28, 
            fontWeight: 600, 
            color: '#18365E', 
            margin: 0,
            fontFamily: 'Montserrat, sans-serif'
          }}>
            Use Case Triage Results
          </h1>
          <p style={{ 
            fontSize: 14, 
            color: '#6b7280', 
            marginTop: 4,
            margin: '4px 0 0 0'
          }}>
            {processedUseCases.length} of {allUseCases.length} use cases shown
            {categoryMode === 'client' && ' (Client Categories)'}
            {' • '}
            <span style={{ color: '#059669' }}>Scoring v1-triage-2025-09-29</span>
          </p>
        </div>
        <button
          onClick={() => navigate('/matrix?' + searchParams.toString())}
          style={{
            fontSize: 14,
            color: '#18365E',
            background: 'none',
            border: 'none',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: '8px 12px'
          }}
          onMouseEnter={e => e.target.style.color = '#FF6E4C'}
          onMouseLeave={e => e.target.style.color = '#18365E'}
        >
          Switch to Matrix View →
        </button>
      </div>

      {/* Filters Bar */}
      <FiltersToolbar
        filters={filters}
        onChange={handleFiltersChange}
        uniqueTypes={uniqueTypes}
        uniqueCoreCategories={uniqueCoreCategories}
        uniqueClientCategories={uniqueClientCategories}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        sortBy={sortBy}
        onSortChange={handleSortChange}
      />

      {/* Error Message */}
      {error && (
        <div style={{
          margin: '16px 24px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          color: '#dc2626',
          fontSize: 14
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Main Content */}
      <div style={{ 
        display: 'flex', 
        gap: 20, 
        padding: '20px 24px', 
        flex: 1,
        minHeight: 0
      }}>
        {/* Results Table */}
        <div style={{ 
          flex: '1 1 70%',
          backgroundColor: 'white',
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 180px 120px 90px 200px',
            padding: '16px 20px',
            backgroundColor: '#f9fafb',
            borderBottom: '2px solid #e5e7eb',
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#6b7280'
          }}>
            <div>Rank</div>
            <div>Use Case</div>
            <div>Category</div>
            <div>Type</div>
            <div>Confidence</div>
            <div style={{ textAlign: 'right' }}>Triage Scores</div>
          </div>

          {/* Table Body */}
          <div style={{ 
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            {processedUseCases.length === 0 ? (
              <div style={{
                padding: 40,
                textAlign: 'center',
                color: '#9ca3af'
              }}>
                No use cases match your filters
              </div>
            ) : (
              processedUseCases.map((uc, index) => {
                const isSelected = selectedUseCase?.use_case_id === uc.use_case_id;
                const isHovered = hoveredUseCase?.use_case_id === uc.use_case_id;
                const isTopPerformer = uc.is_top_performer;
                const hasFinancialGate = uc.has_financial_gate;
                const confidenceColors = getConfidenceColor(uc.confidence_level);
                
                return (
                  <div
                    key={uc.use_case_id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '60px 1fr 180px 120px 90px 200px',
                      padding: '14px 20px',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#fef3c7' : isHovered ? '#f9fafb' : isTopPerformer ? '#f0fdf4' : 'white',
                      borderLeft: isSelected ? '4px solid #FF6E4C' : isTopPerformer ? '4px solid #10b981' : '4px solid transparent',
                      transition: 'all 0.15s ease'
                    }}
                    onClick={() => handleSelectUseCase(uc)}
                    onMouseEnter={() => setHoveredUseCase(uc)}
                    onMouseLeave={() => setHoveredUseCase(null)}
                  >
                    {/* Rank */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        fontSize: 14,
                        fontWeight: 600,
                        backgroundColor: index === 0 ? '#f59e0b' : index < 3 ? '#3b82f6' : index < 10 ? '#10b981' : '#f3f4f6',
                        color: index < 10 ? 'white' : '#374151',
                        position: 'relative'
                      }}>
                        {index + 1}
                        {isTopPerformer && (
                          <span style={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            fontSize: 10,
                            color: '#f59e0b'
                          }}>
                            ⭐
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Title */}
                    <div>
                      <div style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#18365E',
                        marginBottom: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}>
                        {uc.title || uc.name}
                        {hasFinancialGate && (
                          <span 
                            title="Score capped at 40 - no ROI model available"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              backgroundColor: '#fef2f2',
                              border: '1px solid #fca5a5',
                              color: '#dc2626',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'help'
                            }}
                          >
                            $
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {uc.is_baseline && (
                          <span style={{
                            fontSize: 10,
                            color: '#6b7280',
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: 4,
                            display: 'inline-block'
                          }}>
                            Baseline
                          </span>
                        )}
                        {isTopPerformer && (
                          <span style={{
                            fontSize: 10,
                            color: '#059669',
                            backgroundColor: '#dcfce7',
                            padding: '2px 6px',
                            borderRadius: 4,
                            display: 'inline-block',
                            fontWeight: 600
                          }}>
                            Top 10
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Category */}
                    <div style={{
                      fontSize: 13,
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      {categoryMode === 'client' && uc.client_category_names?.length > 0 ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {uc.client_category_names.slice(0, 2).map(catName => (
                            <span 
                              key={catName}
                              style={{
                                fontSize: 11,
                                padding: '3px 8px',
                                borderRadius: 4,
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                fontWeight: 500,
                                border: '1px solid #fcd34d'
                              }}
                            >
                              {catName}
                            </span>
                          ))}
                          {uc.client_category_names.length > 2 && (
                            <span style={{
                              fontSize: 10,
                              color: '#92400e'
                            }}>
                              +{uc.client_category_names.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span>
                          {uc.category_name || uc.category || '—'}
                        </span>
                      )}
                    </div>

                    {/* Type */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 500,
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid',
                        display: 'inline-block',
                        ...(uc.type === 'Direct' || uc.priority_label === 'Direct' ? {
                          backgroundColor: '#dcfce7',
                          borderColor: '#86efac',
                          color: '#166534'
                        } : {
                          backgroundColor: '#dbeafe',
                          borderColor: '#93c5fd',
                          color: '#1e40af'
                        })
                      }}>
                        {uc.type || uc.priority_label || '—'}
                      </span>
                    </div>

                    {/* Confidence Level */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '4px 8px',
                        borderRadius: 4,
                        textTransform: 'uppercase',
                        backgroundColor: confidenceColors.bg,
                        border: `1px solid ${confidenceColors.border}`,
                        color: confidenceColors.text
                      }}>
                        {uc.confidence_level || 'Medium'}
                      </span>
                    </div>

                    {/* Triage Scores */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 8
                    }}>
                      <ScoreChip label="V" value={uc.value_score} max={100} color="#3b82f6" />
                      <ScoreChip label="I" value={uc.implementation_score} max={100} color="#10b981" />
                      <ScoreChip 
                        label="T" 
                        value={uc.total_score} 
                        max={200} 
                        color="#FF6E4C" 
                        emphasis 
                        capped={hasFinancialGate}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Preview Panel */}
        <div style={{ 
          flex: '0 0 30%',
          minWidth: 320,
          maxWidth: 400,
          position: 'sticky',
          top: 20,
          height: 'fit-content'
        }}>
          <UseCasePreview
            useCase={selectedUseCase}
            rank={selectedUseCase ? processedUseCases.findIndex(uc => uc.use_case_id === selectedUseCase.use_case_id) + 1 : null}
            displayMode="Triage"
            onClear={selectedUseCase ? handleClear : null}
            onStep={selectedUseCase ? handleStep : null}
            onViewProfile={handleViewProfile}
            fetchDetails={fetchDetails}
            compact={true}
            showConfidence={true}
            showFinancialGate={selectedUseCase?.has_financial_gate}
          />
        </div>
      </div>
    </div>
  );
}

// Score Chip Component
function ScoreChip({ label, value, max, color, emphasis, capped }) {
  const hasValue = Number.isFinite(value);
  const displayValue = hasValue ? Math.round(value) : '—';
  
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: emphasis ? 600 : 500,
        backgroundColor: hasValue ? `${color}15` : '#f3f4f6',
        color: hasValue ? color : '#9ca3af',
        border: `1px solid ${hasValue ? `${color}30` : '#e5e7eb'}`,
        position: 'relative',
        minWidth: 55,
        ...(emphasis && hasValue ? {
          boxShadow: `0 0 0 3px ${color}10`
        } : {}),
        ...(capped ? {
          borderStyle: 'dashed',
          opacity: 0.8
        } : {})
      }}
      title={`${label}: ${displayValue} / ${max}${capped ? ' (Capped - No ROI Model)' : ''}`}
    >
      <span style={{ fontSize: 10, opacity: 0.8 }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{displayValue}</span>
      {capped && (
        <span style={{
          position: 'absolute',
          top: -2,
          right: -2,
          fontSize: 8,
          color: '#dc2626'
        }}>
          ⚠
        </span>
      )}
    </div>
  );
}